import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext.jsx';
import {
  isPushSupported,
  iosNeedsInstallForPush,
  subscribeToPush,
  getPushUnsupportedReason,
} from '../../utils/push.js';
import { getVapidPublicKey, savePushSubscription } from '../../services/notifications.service.js';
import './PushPromptBanner.css';

const DISMISS_KEY = 'notif_push_prompt_dismissed_at';
const REASK_DAYS = 7;

function recentlyDismissed() {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!ts) return false;
  return Date.now() - ts < REASK_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Contextual soft-prompt to enable Web Push. Shows once authenticated, when the
 * browser permission is still "default" and the user hasn't dismissed it lately.
 * On iOS-in-a-tab it shows an "Add to Home Screen" hint instead (iOS push needs
 * the installed PWA).
 *
 * The banner is hidden when the server has no VAPID keys or push is unsupported
 * in the current context (e.g. HTTP on Linux without TLS).
 */
export default function PushPromptBanner() {
  const { isAuthenticated } = useAuth();
  const [visible, setVisible] = useState(false);
  const [needsInstall, setNeedsInstall] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      setVisible(false);
      return undefined;
    }
    if (recentlyDismissed()) {
      setVisible(false);
      return undefined;
    }
    if ('Notification' in window && Notification.permission !== 'default') {
      setVisible(false);
      return undefined;
    }

    const installHint = iosNeedsInstallForPush();
    if (!isPushSupported() && !installHint) {
      setVisible(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      // iOS Safari tab: show install hint only (no VAPID call needed).
      if (installHint) {
        if (!cancelled) {
          setNeedsInstall(true);
          setVisible(true);
        }
        return;
      }

      try {
        const keyRes = await getVapidPublicKey();
        if (cancelled) return;
        if (!keyRes.success || !keyRes.data?.publicKey) {
          // Server push not configured — do not show a prompt that cannot succeed.
          setVisible(false);
          return;
        }
        setNeedsInstall(false);
        setVisible(true);
      } catch {
        if (!cancelled) setVisible(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }, []);

  const enable = useCallback(async () => {
    setError('');
    setBusy(true);
    try {
      const unsupported = getPushUnsupportedReason();
      if (unsupported) {
        setError(unsupported);
        return;
      }

      const keyRes = await getVapidPublicKey();
      if (!keyRes.success || !keyRes.data?.publicKey) {
        setError(
          keyRes.error
            || 'Configuration des notifications indisponible. Contactez l’administrateur du site.'
        );
        return;
      }
      const subscription = await subscribeToPush(keyRes.data.publicKey);
      await savePushSubscription(subscription);
      setVisible(false);
    } catch (err) {
      setError(err.message || 'Impossible d’activer les notifications.');
    } finally {
      setBusy(false);
    }
  }, []);

  if (!visible) return null;

  return (
    <div className="push-prompt" role="region" aria-label="Activer les notifications">
      <span className="push-prompt-icon" aria-hidden="true">🔔</span>
      <div className="push-prompt-text">
        <strong>Restez informé</strong>
        <span>
          {needsInstall
            ? "Sur iPhone/iPad : ouvrez le menu Partager puis « Sur l’écran d’accueil » pour recevoir les notifications."
            : 'Activez les notifications pour suivre vos demandes et validations.'}
        </span>
        {error && <span className="push-prompt-error" role="alert">{error}</span>}
      </div>
      <div className="push-prompt-actions">
        {!needsInstall && (
          <button type="button" className="btn-primary btn-sm" onClick={enable} disabled={busy}>
            {busy ? 'Activation…' : 'Activer'}
          </button>
        )}
        <button type="button" className="btn-ghost btn-sm" onClick={dismiss} disabled={busy}>
          Plus tard
        </button>
      </div>
    </div>
  );
}
